import { useState, useCallback } from 'react';

import type { ComponentColorOrDefault } from '../../utility/types';
import { Button } from '../button';

import { Modal } from './Modal';
import { SimpleModalContent } from './SimpleModalContent';
import type { SimpleModalContentWidth } from './SimpleModalContent';

interface UseConfirmModalProps {
  /**
   * An emblem above the title, for a confirm whose consequence the copy alone
   * understates.
   *
   * A node rather than an icon name, so a caller picks the glyph, the size and
   * the colour together — the three are one decision, and a modal that took
   * only a name would have callers styling it from the outside.
   */
  icon?: React.ReactNode;
  title?: string;
  body?: React.ReactNode;
  cancelLabel?: React.ReactNode;
  confirmLabel?: React.ReactNode;
  /**
   * Confirm handler. If the function returns a thenable, the modal stays open
   * and disables its buttons until the promise settles — closes on
   * resolution, stays open on rejection so callers can surface the error and
   * let the user retry.
   *
   * Typed as `() => void` to stay backward-compatible with callers whose
   * handlers return non-promise values (e.g. `setTimeout`); the hook
   * inspects the actual return value at runtime.
   */
  onConfirm?: () => void;
  /**
   * The confirm button's colour. `Red` for an action that destroys something or
   * stops something running.
   *
   * Defaulted rather than inferred from the presence of `icon`: a modal can
   * want the emblem without the button, and tying the two would make one of
   * them unreachable.
   */
  confirmColor?: ComponentColorOrDefault;
  onCancel?: () => void;
  /**  bypass confirm dialog if true */
  autoConfirm?: boolean;
  /** Width of the modal content (default 320). Use 400 for longer labels. */
  contentWidth?: SimpleModalContentWidth;
  /**
   * Extra classes for both action buttons (e.g. `tw-rounded-full` for the
   * redesign's capsule buttons). Defaults to the legacy `tw-rounded-6`.
   */
  buttonClassName?: string;
  /**
   * Base testid for the modal and its two buttons, e.g. `${testId}`,
   * `${testId}-cancel-button`, `${testId}-confirm-button`. Defaults to
   * `'confirm-modal'` for backward compatibility — every caller sharing that
   * default is why a closed-but-mounted confirm modal from one caller can
   * collide with an open one from another; opt into a distinct `testId` at
   * high-traffic call sites to make that collision structurally impossible.
   */
  testId?: string;
}

export function useConfirmModal(
  props: UseConfirmModalProps,
): [
  JSX.Element,
  (overrideProps?: React.SyntheticEvent | UseConfirmModalProps) => void,
] {
  const [isOpen, setIsOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const [overriddenProps, setOverriddenProps] = useState<
    UseConfirmModalProps | undefined
  >();

  const {
    icon,
    confirmColor,
    title,
    body,
    cancelLabel = 'Cancel',
    confirmLabel = 'Confirm',
    onConfirm,
    onCancel,
    autoConfirm,
    contentWidth,
    buttonClassName = 'tw-rounded-6',
    testId = 'confirm-modal',
  } = { ...props, ...overriddenProps };

  const onCancelWrapper = useCallback(() => {
    if (isProcessing) return;
    setIsOpen(false);
    onCancel?.();
  }, [onCancel, isProcessing]);

  const handleConfirm = async () => {
    if (!onConfirm) {
      setIsOpen(false);

      return;
    }

    const result = onConfirm() as unknown;

    if (!result || typeof (result as { then?: unknown }).then !== 'function') {
      setIsOpen(false);

      return;
    }

    setIsProcessing(true);

    try {
      await (result as Promise<unknown>);
      setIsOpen(false);
    } catch {
      // Rejection keeps the modal open so the user can retry.
    } finally {
      setIsProcessing(false);
    }
  };

  return [
    <Modal data-testid={testId} isOpen={isOpen} onClose={onCancelWrapper}>
      <SimpleModalContent
        icon={icon}
        title={title}
        width={contentWidth}
        buttons={
          <>
            <Button
              data-testid={`${testId}-cancel-button`}
              className={buttonClassName}
              onClick={onCancelWrapper}
              disabled={isProcessing}
            >
              {cancelLabel}
            </Button>
            {onConfirm && (
              <Button
                data-testid={`${testId}-confirm-button`}
                variant="Filled"
                color={confirmColor}
                className={buttonClassName}
                onClick={handleConfirm}
                disabled={isProcessing}
              >
                {isProcessing ? 'Loading...' : confirmLabel}
              </Button>
            )}
          </>
        }
      >
        {body}
      </SimpleModalContent>
    </Modal>,
    (overrideProps?: UseConfirmModalProps | React.SyntheticEvent) => {
      if (
        autoConfirm ||
        (overrideProps &&
          'autoConfirm' in overrideProps &&
          (overrideProps as UseConfirmModalProps).autoConfirm)
      ) {
        onConfirm?.();
      } else {
        if (overrideProps && 'type' in overrideProps) {
          // ignore parameter if it's a DOM event (e.g. `onClick={openConfirmModal}`)
          setOverriddenProps(undefined);
        } else {
          setOverriddenProps(overrideProps);
        }

        setIsOpen(true);
      }
    },
  ];
}
